/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : VehicleStatus.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import deleteIcon from "../../assets/delete_icon.png";
import BorderColorIcon from "@mui/icons-material/BorderColor";

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";

import { useDispatch, useSelector } from "react-redux";

import customConsole from "../../config/customConsole";
import { HandleApiActions } from "../../redux/actions/actionHandler";
import profileActions from "../../redux/actions/profile";
import adminSettingsAPI from "../../redux/actions/adminSetttingAPI";
import { convertUtcToTimeZone } from "../../utils/convertUtcToTimeZone";
import { DialogContentText, IconButton } from "@mui/material";
import CommonBreadcrumbs from "../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import CustomSearchbarWithApiV2 from "../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import CustomDataGridV2 from "../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CommonInputSwitch from "../../components/customComponentsV2/common/InputSwitch/CommonInputSwitch";
import * as XLSX from "xlsx";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import PropTypes from "prop-types";
import CustomCheckBoxV2 from "../../components/customComponentsV2/common/checkBox/CustomCheckBoxV2";

const getVehicleStatusList = (resp) => {
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.response?.data)) return resp.response.data;
  return [];
};

const selectedVehicleStatusShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  name: PropTypes.string,
  sequence: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  whole_vehicle: PropTypes.bool,
  whole_vehicle_verification_relevant: PropTypes.bool,
  allData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    is_edit_allowed: PropTypes.bool,
    whole_vehicle: PropTypes.bool,
    whole_vehicle_verification_relevant: PropTypes.bool,
    is_whole_vehicle_verification_relevant: PropTypes.bool,
  }),
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    sequence: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    whole_vehicle: PropTypes.bool,
    whole_vehicle_verification_relevant: PropTypes.bool,
    allData: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      is_edit_allowed: PropTypes.bool,
      whole_vehicle: PropTypes.bool,
      whole_vehicle_verification_relevant: PropTypes.bool,
      is_whole_vehicle_verification_relevant: PropTypes.bool,
    }),
  }),
});

const getWholeVehicleVerificationRelevant = (data) =>
  Boolean(
    data?.whole_vehicle ??
      data?.whole_vehicle_verification_relevant ??
      data?.is_whole_vehicle_verification_relevant ??
      false,
  );

function VehicleStatus({ open, handleClick }) {
  // Add resize detection state
  const [tableKey, setTableKey] = useState(0);
  const [totalVehicleStatusCount, setTotalVehicleStatusCount] = useState(0);

  // Force table re-render when component becomes active
  useEffect(() => {
    if (open === "vehicle-status") {
      setTableKey((prev) => prev + 1);
    }
  }, [open]);

  // Add window resize listener
  useEffect(() => {
    const handleResize = () => {
      setTableKey((prev) => prev + 1);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const dispatch = useDispatch();

  const vehicleStatusDetails = useSelector(
    (state) => state.vehicleStatusReducer,
  );

  const timeZoneDetails = useSelector((state) => state.profileReducer);

  const [page, setPage] = React.useState(1); // Changed to 1-based pagination like VehicleFunction
  const [rowsPerPage] = React.useState(25);
  const [getVehicleStatus, setGetVehicleStatus] = React.useState([]);
  const [openAddVehicleStatusDialog, setOpenAddVehicleStatusDialog] =
    React.useState(false);
  const [openEditVehicleStatusDialog, setOpenEditVehicleStatusDialog] =
    React.useState(false);
  const [openDeleteVehicleStstusDialog, setOpenDeleteVehicleStstusDialog] =
    React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [tableRowsData, setTableRowsData] = useState([]);
  const [searchQuery] = useState("");

  const supplierRef = useRef(null);
  useEffect(() => {
    if (open === "8") {
      const timeout = setTimeout(() => {
        if (
          supplierRef.current &&
          supplierRef.current.scrollHeight > supplierRef.current.clientHeight
        ) {
          supplierRef.current.scrollTop = supplierRef.current.scrollHeight;
        }
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...profileActions.get_coutry_regions,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    const vehicleStatusList = getVehicleStatus
      ?.map((item, index) => ({
        sno: (page - 1) * rowsPerPage + index + 1,
        id: item?.id || index,
        name: item?.name,
        sequence: item?.sequence,
        is_edit_allowed: item?.is_edit_allowed ? "YES" : "NO",
        whole_vehicle_verification_relevant:
          getWholeVehicleVerificationRelevant(item) ? "YES" : "NO",
        is_approval_required: item?.is_approval_required,
        created_at: convertUtcToTimeZone(
          item?.created_at,
          timeZoneDetails?.profile?.profile?.region,
        ),
        updated_at: convertUtcToTimeZone(
          item?.updated_at,
          timeZoneDetails?.profile?.profile?.region,
        ),
        allData: item,
      }))
      .sort((a, b) => a.sequence - b.sequence);

    setTableRowsData(vehicleStatusList || []); // Ensure it's always an array
  }, [getVehicleStatus, page, rowsPerPage, timeZoneDetails]);

  // API call function with pagination support similar to VehicleFunction
  const getVehicleStatusDetails = useCallback(
    async (pageOverride) => {
      const pageToUse = pageOverride ?? page;
      const resp = await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.get_vehicle_status,
          show_toast: false,
          params: {
            page_number: pageToUse,
            page_size: rowsPerPage,
          },
        }),
      );

      if (resp?.error) {
        setGetVehicleStatus([]);
        setTableRowsData([]);
        setTotalVehicleStatusCount(0);
        return { list: [], totalCount: 0 };
      }

      const list = getVehicleStatusList(resp);

      const totalCount =
        resp?.total_count ??
        resp?.totalItems ??
        resp?.total ??
        (Array.isArray(list) ? list.length : 0);

      setGetVehicleStatus(list);
      setTotalVehicleStatusCount(totalCount);

      return { list, totalCount };
    },
    [dispatch, page, rowsPerPage],
  );

  // Effect to fetch data when pagination changes
  useEffect(() => {
    getVehicleStatusDetails();
  }, [page, rowsPerPage, getVehicleStatusDetails]);

  useEffect(() => {
    customConsole("VehicleStatus Details");
    customConsole(vehicleStatusDetails);
  }, [vehicleStatusDetails]);

  const COLUMNS = useMemo(
    () => [
      {
        field: "sequence",
        headerName: "Sequence",
        flex: 0.3,
        minWidth: 80,
      },
      {
        field: "name",
        headerName: "Name",
        flex: 0.8,
        minWidth: 120,
      },
      {
        field: "is_edit_allowed",
        headerName: "Is Editable",
        flex: 0.5,
        minWidth: 100,
      },
      // {
      //   field: "whole_vehicle_verification_relevant",
      //   headerName: "Whole Vehicle Verification Relevant",
      //   flex: 0.8,
      //   minWidth: 210,
      // },
      {
        field: "created_at",
        headerName: "Created At",
        flex: 0.6,
        minWidth: 100,
      },
      {
        field: "updated_at",
        headerName: "Updated At",
        flex: 0.6,
        minWidth: 100,
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.4,
        minWidth: 90,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              size="small"
              sx={{ color: "#000000", p: 0.5 }}
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(params);
              }}
            >
              <BorderColorIcon fontSize="inherit" sx={{ fontSize: 19 }} />
            </IconButton>
            <IconButton
              size="small"
              sx={{ p: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(params);
              }}
            >
              <img
                src={deleteIcon}
                alt="delete"
                style={{ width: 24, height: 24 }}
              />
            </IconButton>
          </Box>
        ),
      },
    ],
    [],
  );

  const toggleDialog = (newOpen) => () => {
    setOpenAddVehicleStatusDialog(newOpen);
  };

  const handleAddVehicleStatusClick = () => {
    toggleDialog(true)();
  };

  // removed unused handleTableRowClick; using handleEditClick directly

  // Function to handle Edit button click
  const handleEditClick = (params) => {
    setSelectedRow(params);
    customConsole("Edit clicked for row:", params);
    setOpenEditVehicleStatusDialog(true);
  };

  // Function to handle Delete button click
  const handleDeleteClick = (params) => {
    setSelectedRow(params);
    customConsole("Delete clicked for row:", params);
    setOpenDeleteVehicleStstusDialog(true);
  };

  const filteredRows = useMemo(() => {
    if (!searchQuery) return tableRowsData;
    return tableRowsData.filter((row) =>
      COLUMNS.some((column) =>
        row[column.field]
          ?.toString()
          .toLowerCase()
          .includes(searchQuery.toLowerCase()),
      ),
    );
  }, [searchQuery, tableRowsData, COLUMNS]);

  // Reset pagination when search query changes
  useEffect(() => {
    setPage(1); // Reset to first page when search changes
  }, [searchQuery]);

  // Get paginated rows for current page
  // removed unused paginatedRows

  // Function to handle export to Excel
  const handleExportToExcel = () => {
    if (!filteredRows || filteredRows.length === 0) {
      console.warn("No data to export");
      return;
    }

    // Prepare data for export - map to clean format without internal fields
    const exportData = filteredRows.map((row) => ({
      Sequence: row.sequence,
      Name: row.name,
      "Is Editable": row.is_edit_allowed,
      "Whole Vehicle Verification Relevant":
        row.whole_vehicle_verification_relevant,
      Created: row.created_at,
      Updated: row.updated_at,
    }));

    // Create a new workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Project Development Milestone");

    // Generate and download Excel file
    XLSX.writeFile(wb, "Project_Development_Milestone.xlsx");
  };

  return (
    <Box
      sx={{
        margin: 0,
        padding: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#F5F5F5",
      }}
    >
      {/* Breadcrumbs and description */}
      <Box
        sx={{
          padding: { xs: "8px 8px 0px 8px", md: "12px 12px 0px 12px" },
          flexShrink: 0,
        }}
      >
        <CommonBreadcrumbs
          items={[
            { label: "Settings" },
            { label: "Feature Portfolio" },
            { label: "Project Development Milestone" },
          ]}
        />
      </Box>

      {/* Header with Search and Add Button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: { xs: "0 16px 12px 16px", md: "0 24px 16px 24px" },
          flexShrink: 0,
          mt: 1,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <Box>
            <CustomSearchbarWithApiV2
              params={{
                page_number: page,
                page_size: rowsPerPage,
              }}
              apiAction={adminSettingsAPI.get_vehicle_status}
              searchKey={"name"}
              getDataOnSearch={(data, resp) => {
                console.log("Search Data:", resp);
                if (resp?.error) {
                  setGetVehicleStatus([]);
                  setTableRowsData([]);
                  setTotalVehicleStatusCount(0);
                  return;
                }

                const searchedList = Array.isArray(data) ? data : [];
                setGetVehicleStatus(searchedList);
                setTotalVehicleStatusCount(
                  resp?.total_count ??
                    resp?.totalItems ??
                    resp?.total ??
                    searchedList.length,
                );
              }}
              placeholder={"Search"}
            />
          </Box>
          <Box>
            <AddButtonV2 handleOnClick={handleAddVehicleStatusClick} />
          </Box>
        </Box>
        <Box>
          <CustomButtonV2
            startIcon={<FileDownloadOutlinedIcon />}
            type="submit"
            size="small"
            btnName="Export"
            handleOnClick={handleExportToExcel}
          />
        </Box>
      </Box>

      {/* DataGrid Container */}
      <Box
        sx={{
          height: "76vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          padding: { xs: "0 8px", md: "0 16px" },
          mt: -1,
        }}
        key={`vehicle-status-table-${tableKey}`}
      >
        <CustomDataGridV2
          rows={filteredRows}
          columns={COLUMNS}
          isMultiLine={true}
          onRowClick={handleEditClick}
          showPagination={true}
          page={page}
          rowsPerPage={rowsPerPage}
          handlePageChange={(newPage) => setPage(newPage)}
          totalItems={totalVehicleStatusCount}
        />
      </Box>

      {/* Dialogs */}
      <AddVehicleStatus
        open={openAddVehicleStatusDialog}
        toggleDialog={toggleDialog}
        setPage={setPage}
        refreshVehicleStatus={getVehicleStatusDetails}
      />
      <EditVehicleStatus
        open={openEditVehicleStatusDialog}
        setOpenEditVehicleStatusDialog={setOpenEditVehicleStatusDialog}
        selectedRow={selectedRow}
        page={page}
        refreshVehicleStatus={getVehicleStatusDetails}
      />
      <DeleteVehicleStstus
        openDeleteVehicleStstusDialog={openDeleteVehicleStstusDialog}
        setOpenDeleteVehicleStstusDialog={setOpenDeleteVehicleStstusDialog}
        selectedRow={selectedRow}
        setPage={setPage}
        page={page}
        rowsPerPage={rowsPerPage}
        refreshVehicleStatus={getVehicleStatusDetails}
      />
    </Box>
  );
}

export default VehicleStatus;

VehicleStatus.propTypes = {
  open: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleClick: PropTypes.func,
};

const AddVehicleStatus = ({
  open,
  toggleDialog,
  setPage,
  refreshVehicleStatus,
}) => {
  const dispatch = useDispatch();

  const [name, setName] = useState("");
  // removed unused nameError state
  const [sequence, setSequence] = useState();
  // removed unused sequenceError state
  const [isEditable, setIsEditable] = useState(false);
  const [
    wholeVehicleVerificationRelevant,
    setWholeVehicleVerificationRelevant,
  ] = useState(false);
  // removed unused isEditableError state
  // removed unused selectedValue state

  const handleClose = () => {
    setName("");
    setSequence("");
    setIsEditable(false);
    setWholeVehicleVerificationRelevant(false);
    toggleDialog(false)();
  };

  const handleClickAdd = async () => {
    try {
      const reqObj = {
        current_time: new Date(),
        name: name,
        sequence: Number(sequence),
        is_edit_allowed: isEditable,
        whole_vehicle: wholeVehicleVerificationRelevant,
      };

      await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.add_vehicle_status,
          params: reqObj,
          show_toast: true,
        }),
      );
      toggleDialog(false)();

      setPage(1);
      await refreshVehicleStatus(1);

      // Reset form fields
      setName("");
      setSequence("");
      setIsEditable(false);
      setWholeVehicleVerificationRelevant(false);
    } catch (error) {
      console.error("Error adding vehicle status:", error);
    }
  };

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={handleClose}
      title={"Add Project Development Milestone"}
      successButtonName={"Add"}
      rejectButtonName={"Close"}
      onClickSuccess={handleClickAdd}
      onDisabled={
        !(
          name?.length > 2 &&
          name?.trim() !== "" &&
          sequence &&
          Number(sequence) > 0
        )
      }
      actionButtonStyle={"continuous"}
      onClickReject={handleClose}
    >
      <Stack spacing={2} sx={{ width: "350px" }}>
        <CustomFormTextFieldV2
          label="Name"
          required={true}
          value={name}
          setValue={(val) => {
            if (val.length <= 35) {
              setName(val);
            }
          }}
          maxLength={35}
        />
        <CustomFormTextFieldV2
          label="Sequence"
          required={true}
          value={sequence}
          setValue={(val) => {
            // Only allow numbers
            if (/^\d*$/.test(val)) {
              setSequence(val);
            }
          }}
          fldType={"text1"}
          countUnit={"number"}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CommonInputSwitch
            checked={isEditable || false}
            onChange={setIsEditable}
            label="Is Editable"
          />
          <Typography
            sx={{
              fontFamily: "Open Sans",
              fontSize: "14px",
              minWidth: "30px",
            }}
          >
            {isEditable ? "YES" : "NO"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CustomCheckBoxV2
            value={wholeVehicleVerificationRelevant}
            onChange={(e) =>
              setWholeVehicleVerificationRelevant(e.target.checked)
            }
            label="Whole Vehicle Verification Relevant"
          />
        </Box>
      </Stack>
    </CustomDialogBoxV2>
  );
};

AddVehicleStatus.propTypes = {
  open: PropTypes.bool,
  toggleDialog: PropTypes.func,
  setPage: PropTypes.func,
  refreshVehicleStatus: PropTypes.func,
};

const EditVehicleStatus = ({
  open,
  setOpenEditVehicleStatusDialog,
  selectedRow,
  page,
  refreshVehicleStatus,
}) => {
  const dispatch = useDispatch();
  // Form states
  const [editName, setEditName] = useState("");
  const [editSequence, setEditSequence] = useState("");
  const [editIsEditable, setEditIsEditable] = useState(false);
  const [initialIsEditable, setInitialIsEditable] = useState(false);
  const [
    editWholeVehicleVerificationRelevant,
    setEditWholeVehicleVerificationRelevant,
  ] = useState(false);
  const [
    initialWholeVehicleVerificationRelevant,
    setInitialWholeVehicleVerificationRelevant,
  ] = useState(false);

  // Populate fields when `selectedRow` changes
  useEffect(() => {
    if (selectedRow) {
      console.log("selectedRow in EditVehicleStatus:", selectedRow);

      // Handle different data structures
      const rowData = selectedRow.row || selectedRow;

      setEditName(rowData?.name || "");
      setEditSequence(rowData?.sequence || "");
      const initial = rowData?.allData?.is_edit_allowed ?? false;
      const initialWholeVehicleRelevant =
        getWholeVehicleVerificationRelevant(rowData?.allData || rowData);

      setEditIsEditable(initial);
      setInitialIsEditable(initial);
      setEditWholeVehicleVerificationRelevant(initialWholeVehicleRelevant);
      setInitialWholeVehicleVerificationRelevant(
        initialWholeVehicleRelevant,
      );
    }
  }, [selectedRow]);

  const isEditableChanged = editIsEditable !== initialIsEditable;
  const wholeVehicleVerificationRelevantChanged =
    editWholeVehicleVerificationRelevant !==
    initialWholeVehicleVerificationRelevant;

  // Handle dialog close
  const handleClose = () => {
    setOpenEditVehicleStatusDialog(false);
  };

  // Handle the edit submission
  const handleClickEdit = async () => {
    try {
      // Handle different data structures
      const rowData = selectedRow?.row || selectedRow;
      // Create the request object
      const reqObj = {
        id: rowData?.allData?.id || rowData?.id,
        name: editName,
        sequence: editSequence,
        is_edit_allowed: editIsEditable,
        whole_vehicle: editWholeVehicleVerificationRelevant,
        current_time: new Date().toISOString(),
      };

      const resp = await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.update_vehicle_status,
          params: reqObj,
          show_toast: true,
        }),
      );

      if (!resp.error) {
        handleClose();
      }
      await refreshVehicleStatus(page);
    } catch (error) {
      console.error("Error updating vehicle status:", error);
    }
  };

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={handleClose}
      title={"Update Project Development Milestone"}
      successButtonName={"Update"}
      rejectButtonName={"Close"}
      onClickSuccess={handleClickEdit}
      onDisabled={
        !selectedRow ||
        !(isEditableChanged || wholeVehicleVerificationRelevantChanged)
      }
      actionButtonStyle={"continuous"}
      onClickReject={handleClose}
    >
      <Stack spacing={2} sx={{ width: "350px" }}>
        {/* Name field - Disabled for editing */}
        <CustomFormTextFieldV2
          label="Name"
          value={editName}
          setValue={setEditName}
          required={true}
          disabled={true}
        />

        {/* Sequence field - Disabled for editing */}
        <CustomFormTextFieldV2
          label="Sequence"
          value={editSequence}
          setValue={setEditSequence}
          required={true}
          disabled={true}
        />

        {/* Is Editable Field as Switch */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CommonInputSwitch
            checked={editIsEditable || false}
            onChange={(value) => setEditIsEditable(value)}
            label="Is Editable"
            required={true}
          />
          <Typography
            sx={{
              fontFamily: "Open Sans",
              fontSize: "14px",
              minWidth: "30px",
            }}
          >
            {editIsEditable ? "YES" : "NO"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CustomCheckBoxV2
            value={editWholeVehicleVerificationRelevant}
            onChange={(e) =>
              setEditWholeVehicleVerificationRelevant(e.target.checked)
            }
            label="Whole Vehicle Verification Relevant"
          />
        </Box>
      </Stack>
    </CustomDialogBoxV2>
  );
};

EditVehicleStatus.propTypes = {
  open: PropTypes.bool,
  setOpenEditVehicleStatusDialog: PropTypes.func,
  selectedRow: selectedVehicleStatusShape,
  page: PropTypes.number,
  refreshVehicleStatus: PropTypes.func,
};

const DeleteVehicleStstus = ({
  openDeleteVehicleStstusDialog,
  selectedRow,
  setOpenDeleteVehicleStstusDialog,
  setPage,
  page,
  rowsPerPage,
  refreshVehicleStatus,
}) => {
  const dispatch = useDispatch();

  const handleDelete = async () => {
    try {
      const reqObj = {
        name: selectedRow?.row?.name,
        current_time: new Date(),
      };

      await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.delete_vehicle_status,
          params: reqObj,
          show_toast: true,
        }),
      );
      setOpenDeleteVehicleStstusDialog(false);

      const refreshResult = await refreshVehicleStatus(page);
      const totalCount = refreshResult?.totalCount ?? 0;
      if (page > 1 && totalCount <= (page - 1) * rowsPerPage) {
        const newPage = page - 1;
        setPage(newPage);
        await refreshVehicleStatus(newPage);
      }
    } catch (error) {
      console.error("Error deleting vehicle status:", error);
    }
  };
  return (
    <CustomDialogBoxV2
      open={openDeleteVehicleStstusDialog}
      onClose={() => setOpenDeleteVehicleStstusDialog(false)}
      title={"Delete"}
      successButtonName={"Ok"}
      rejectButtonName={"Cancel"}
      onClickSuccess={handleDelete}
      onClickReject={() => setOpenDeleteVehicleStstusDialog(false)}
      actionButtonStyle={"continuous"}
    >
      <Box
        sx={{
          width: "350px",
          height: "50px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <DialogContentText>Are you sure you want to delete?</DialogContentText>
      </Box>
    </CustomDialogBoxV2>
  );
};

DeleteVehicleStstus.propTypes = {
  openDeleteVehicleStstusDialog: PropTypes.bool,
  selectedRow: selectedVehicleStatusShape,
  setOpenDeleteVehicleStstusDialog: PropTypes.func,
  setPage: PropTypes.func,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  refreshVehicleStatus: PropTypes.func,
};
